import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faBan, faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../UI/V3/Button/Button';
import SearchBar from '../../../UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import styles from './ApiKeysContent.module.scss';
import AddApiKeyModal from '../Add/AddApiKeyModal';
import SecretRevealModal from '../Secret/SecretRevealModal';
import { fetchApiKeysThunk, revokeApiKeyThunk, rotateApiKeySecretThunk } from 'actions/apiKeys/ApiKeysActions';
import { fetchOrganizationsThunk } from 'actions/organization/OrganizationActions';
import { fetchLocationsThunk } from 'actions/location/LocationActions';
import { showSuccessThunk } from '@spike/notifications-action';
import { RootState } from 'store';
import { RootAction } from 'actions/ActionTypes';
import { ApiKey } from 'model/apiKeys';

/**
 * Format date string to "MMMM dd, yyyy" format
 * @param dateString - ISO date string or any parseable date string
 * @returns Formatted date string (e.g., "January 15, 2024")
 */
const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';

    try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) return 'Invalid Date';

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return 'Invalid Date';
    }
};

/**
 * The secret only exists in readable form between the create/rotate response
 * and the moment the reveal modal closes. It is held here, in component state,
 * and never dispatched to the store.
 */
interface RevealedSecret {
    apiKeyName: string;
    clientId: string | null;
    secret: string;
    rotated: boolean;
}

export const ApiKeysContent: React.FC = () => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { organizations } = useSelector((state: RootState) => state.organizations);
    const { apiKeys, loading } = useSelector((state: RootState) => state.apiKeys);

    const [searchText, setSearchText] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
    const [revealedSecret, setRevealedSecret] = useState<RevealedSecret | null>(null);
    const [apiKeyToRevoke, setApiKeyToRevoke] = useState<ApiKey | null>(null);
    const [apiKeyToRotate, setApiKeyToRotate] = useState<ApiKey | null>(null);

    // The parent organization owns the API Keys, same rule the Users tab uses.
    const organizationId = useMemo(
        () => organizations.find(organization => !organization.parentOrganizationId)?.id,
        [organizations]
    );

    useEffect(() => {
        if (organizations.length === 0) {
            dispatch(fetchOrganizationsThunk());
        }
    }, [dispatch, organizations.length]);

    useEffect(() => {
        if (organizationId) {
            dispatch(fetchApiKeysThunk(organizationId));
            dispatch(fetchLocationsThunk());
        }
    }, [dispatch, organizationId]);

    const filteredApiKeys = apiKeys.filter(apiKey => {
        if (!searchText) return true;

        const searchLower = searchText.toLowerCase();
        return (
            apiKey.name.toLowerCase().includes(searchLower) ||
            (apiKey.clientId ?? '').toLowerCase().includes(searchLower)
        );
    });

    const refresh = () => {
        if (organizationId) {
            dispatch(fetchApiKeysThunk(organizationId));
        }
    };

    const handleCreate = () => {
        setEditingApiKey(null);
        setIsAddModalOpen(true);
    };

    const handleEdit = (apiKey: ApiKey) => {
        setEditingApiKey(apiKey);
        setIsAddModalOpen(true);
    };

    const handleCreated = (apiKey: ApiKey, secret: string) => {
        setIsAddModalOpen(false);
        setEditingApiKey(null);
        setRevealedSecret({
            apiKeyName: apiKey.name,
            clientId: apiKey.clientId,
            secret,
            rotated: false
        });
        refresh();
    };

    const handleConfirmRotate = async () => {
        if (!organizationId || !apiKeyToRotate) return;

        try {
            const { apiKey, secret } = await dispatch(rotateApiKeySecretThunk(organizationId, apiKeyToRotate.id));

            setApiKeyToRotate(null);
            setRevealedSecret({
                apiKeyName: apiKey.name,
                clientId: apiKey.clientId,
                secret,
                rotated: true
            });
            refresh();
        } catch (error) {
            // Dialog stays open on failure so the action can be retried.
        }
    };

    const handleConfirmRevoke = async () => {
        if (!organizationId || !apiKeyToRevoke) return;

        try {
            await dispatch(revokeApiKeyThunk(organizationId, apiKeyToRevoke.id));

            setApiKeyToRevoke(null);
            dispatch(showSuccessThunk('API Key revoked') as any);
            refresh();
        } catch (error) {
            // Dialog stays open on failure so the action can be retried.
        }
    };

    const renderLocationAccess = (apiKey: ApiKey): string => {
        if (apiKey.allLocations) return 'All locations';

        const count = apiKey.locationIds.length;
        return count === 1 ? '1 location' : `${count} locations`;
    };

    const renderApiKeysList = () => (
        <div className={styles.apiKeysList}>
            {filteredApiKeys.map(apiKey => (
                <div
                    key={apiKey.id}
                    className={styles.apiKeyCard}
                >
                    <div className={styles.apiKeyHeader}>
                        <div className={styles.apiKeyInfo}>
                            <h3 className={styles.apiKeyName}>
                                {apiKey.name}
                                <span className={apiKey.active ? styles.statusActive : styles.statusRevoked}>
                                    {apiKey.active ? 'Active' : 'Revoked'}
                                </span>
                            </h3>
                            <code className={styles.clientId}>{apiKey.clientId ?? 'No credential'}</code>
                        </div>

                        {apiKey.active && (
                            <div className={styles.apiKeyActions}>
                                <Tooltip
                                    content="Rotate secret"
                                    placement="top"
                                >
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => setApiKeyToRotate(apiKey)}
                                        aria-label="Rotate secret"
                                    >
                                        <FontAwesomeIcon icon={faArrowsRotate} />
                                    </button>
                                </Tooltip>
                                <Tooltip
                                    content="Edit"
                                    placement="top"
                                >
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => handleEdit(apiKey)}
                                        aria-label="Edit"
                                    >
                                        <FontAwesomeIcon icon={faPen} />
                                    </button>
                                </Tooltip>
                                <Tooltip
                                    content="Revoke"
                                    placement="top"
                                >
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => setApiKeyToRevoke(apiKey)}
                                        aria-label="Revoke"
                                    >
                                        <FontAwesomeIcon icon={faBan} />
                                    </button>
                                </Tooltip>
                            </div>
                        )}
                    </div>

                    <div className={styles.apiKeyMeta}>
                        <p className={styles.apiKeyDetail}>
                            <span>Permissions:</span> <strong>{apiKey.scopes.length}</strong> · <span>Access:</span>{' '}
                            <strong>{renderLocationAccess(apiKey)}</strong> · <span>Expires:</span>{' '}
                            <strong>{formatDate(apiKey.expiresAt)}</strong>
                        </p>
                        <p className={styles.apiKeyDate}>
                            <span>Created:</span> <strong>{formatDate(apiKey.createdAt)}</strong>
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderEmptyState = () => (
        <div className={styles.emptyState}>
            <p className={styles.emptyStateTitle}>No API Keys yet</p>
            <p className={styles.emptyStateText}>
                An API Key lets an external system read and update your data through the Cuddles API. Create one for
                each integration so it can be revoked on its own.
            </p>
        </div>
    );

    return (
        <div className={styles.apiKeysContent}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <div>
                        <h1 className={styles.title}>API Keys</h1>
                    </div>
                    <div>
                        <Button
                            label="Create API Key"
                            size="large"
                            variant="outlined"
                            onClick={handleCreate}
                        />
                    </div>
                </div>

                <div className={styles.searchContainer}>
                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={setSearchText}
                        placeholder="Search by name or client ID"
                        className={styles.searchBar}
                    />
                </div>
            </div>

            <div className={styles.content}>
                {loading && apiKeys.length === 0 ? (
                    <div className={styles.loadingContainer}>
                        <Spinner size={32} />
                    </div>
                ) : filteredApiKeys.length === 0 && searchText ? (
                    <div className={styles.noResults}>
                        <p>No API Keys found matching &quot;{searchText}&quot;</p>
                    </div>
                ) : apiKeys.length === 0 ? (
                    renderEmptyState()
                ) : (
                    renderApiKeysList()
                )}
            </div>

            <AddApiKeyModal
                isOpen={isAddModalOpen}
                organizationId={organizationId}
                editingApiKey={editingApiKey}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setEditingApiKey(null);
                }}
                onCreated={handleCreated}
                onUpdated={refresh}
            />

            {revealedSecret && (
                <SecretRevealModal
                    isOpen={true}
                    apiKeyName={revealedSecret.apiKeyName}
                    clientId={revealedSecret.clientId}
                    clientSecret={revealedSecret.secret}
                    rotated={revealedSecret.rotated}
                    onClose={() => setRevealedSecret(null)}
                />
            )}

            {/* Rotation is destructive and immediate, so the dialog states the
                consequence and points at the alternative that avoids it. */}
            <ConfirmDialog
                open={Boolean(apiKeyToRotate)}
                title="Rotate client secret"
                question={
                    apiKeyToRotate
                        ? `Rotating the secret for ${apiKeyToRotate.name} takes effect immediately.`
                        : 'Rotating the secret takes effect immediately.'
                }
                subquestion="Rotation replaces both the client ID and the secret. The current credentials stop working and every access token issued from them is invalidated at once, so the integration is down until the new pair is deployed. To rotate without downtime, create a second API Key, deploy it, then revoke this one."
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Rotate secret"
                onCancel={() => setApiKeyToRotate(null)}
                onConfirm={handleConfirmRotate}
                processing={loading}
                className={styles.confirmDialog}
            />

            <ConfirmDialog
                open={Boolean(apiKeyToRevoke)}
                title="Revoke API Key"
                question={
                    apiKeyToRevoke
                        ? `Are you sure you want to revoke ${apiKeyToRevoke.name}?`
                        : 'Are you sure you want to revoke this API Key?'
                }
                subquestion="It stops working immediately and cannot be reactivated. Any integration using it will lose access."
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Revoke"
                onCancel={() => setApiKeyToRevoke(null)}
                onConfirm={handleConfirmRevoke}
                processing={loading}
                className={styles.confirmDialog}
            />
        </div>
    );
};

export default ApiKeysContent;
